import React, { useEffect, useState } from 'react';
import {
  CafCorpusEntry, CafRequeuePreview, deleteCafCorpusEntry, previewCafEntryRequeue, reactivateCafEntry,
  requeueCafEntryDecisions,
} from '../../../api/cafCorpus';
import { actionCopy } from '../copy';
import { formatCount } from '../format';
import { senderEmail } from '../lenders';
import { Modal } from '../Overlay';
import { MSG_NOT_CONFIRMED, isMissingRoute, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { readBackEntry, useCurrentEntry } from './useCurrentEntry';

interface Props {
  entry: CafCorpusEntry;
  lenderOf: (sender: string) => string | null;
  onClose: () => void;
  onDone: (entry: CafCorpusEntry | null) => void;
}

const nameOf = (entry: CafCorpusEntry, lenderOf: (sender: string) => string | null) =>
  `${lenderOf(entry.sender) ?? senderEmail(entry.sender)}: ${actionCopy(entry.correct_action).label.toLowerCase()}`;

const auditFor = (entry: CafCorpusEntry, reason: string) => ({
  expected_version: entry.version || 0,
  reason: reason.trim(),
  ...(entry.version ? {} : { reviewed_token: entry.compare_token }),
});

const ReasonModal: React.FC<Props & {
  title: string;
  intro: React.ReactNode;
  label: string;
  placeholder: string;
  button: string;
  danger: boolean;
  send: (entry: CafCorpusEntry, reason: string) => Promise<CafCorpusEntry | null>;
  landed: (fresh: CafCorpusEntry, reason: string) => boolean;
  toastText: string;
}> = ({ entry: given, onClose, onDone, title, intro, label, placeholder, button, danger, send, landed, toastText }) => {
  const toast = useToast();
  const { entry, ready } = useCurrentEntry(given);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const go = async () => {
    if (!reason.trim() || saving || !ready) return;
    setSaving(true);
    setProblem('');
    const expected = entry.version || 0;
    const result = await runSave<CafCorpusEntry | null>(() => send(entry, reason), {
      readBack: () => readBackEntry(entry, expected, (fresh) => landed(fresh, reason.trim())),
    });
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      toast.failed(result.message, () => { void go(); });
      return;
    }
    toast.saved(toastText);
    onDone(result.value);
    onClose();
  };
  return (
    <Modal title={title} onClose={onClose} busy={saving}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className={`ui-btn ${danger ? 'ui-btn--danger-solid' : 'ui-btn--primary'} ui-btn--md`}
            disabled={!reason.trim() || saving || !ready} onClick={() => { void go(); }}>
            {saving ? 'Saving…' : button}
          </button>
        </>
      )}>
      <p>{intro}</p>
      <div className="cur-field">
        <label htmlFor={`reason-${entry.id}`}>{label}</label>
        <textarea id={`reason-${entry.id}`} className="cur-input" rows={3} maxLength={2000} value={reason}
          placeholder={placeholder} onChange={(event) => setReason(event.target.value)} disabled={saving} />
      </div>
      {problem && <div className="cur-alert cur-alert--error" role="alert">{problem}</div>}
    </Modal>
  );
};

export const SwitchOffModal: React.FC<Props> = (props) => (
  <ReasonModal {...props} title="Switch off this example?"
    intro={<>The AI stops using <b>{nameOf(props.entry, props.lenderOf)}</b> for new emails straight away. Emails it already handled stay as they are, and the history is kept.</>}
    label="Why are you switching it off?" placeholder="For example: the lender stopped sending these."
    button="Switch off" danger toastText="Switched off. The AI has stopped using this example."
    send={async (entry, reason) => { await deleteCafCorpusEntry(entry.id, auditFor(entry, reason)); return null; }}
    landed={(fresh, reason) => fresh.active === false && fresh.reason === reason} />
);

export const SwitchOnModal: React.FC<Props> = (props) => (
  <ReasonModal {...props} title="Switch this example back on?"
    intro={<>The AI will use <b>{nameOf(props.entry, props.lenderOf)}</b> again for new emails straight away. The history is kept.</>}
    label="Why are you switching it back on?" placeholder="For example: the lender has started sending these again."
    button="Switch back on" danger={false} toastText="Switched back on. The AI is using this example again."
    send={(entry, reason) => reactivateCafEntry(entry.id, auditFor(entry, reason))}
    landed={(fresh, reason) => fresh.active !== false && fresh.reason === reason} />
);

type PreviewState = { kind: 'loading' } | { kind: 'ready'; preview: CafRequeuePreview } | { kind: 'unknown' } | { kind: 'failed' };

export const RerunModal: React.FC<Omit<Props, 'onDone'>> = ({ entry, lenderOf, onClose }) => {
  const toast = useToast();
  const [state, setState] = useState<PreviewState>({ kind: 'loading' });
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  // Final wave item 13: an unconfirmed re-run may still be running server-side and re-runs are
  // never idempotent, so the only way on is Close (as in Fix this email).
  const [unconfirmed, setUnconfirmed] = useState(false);

  useEffect(() => {
    let alive = true;
    previewCafEntryRequeue(entry.id)
      .then((preview) => { if (alive) setState({ kind: 'ready', preview }); })
      .catch((err) => {
        if (!alive) return;
        // An older backend has no count route: re-run as before, without a number.
        setState(isMissingRoute(err) ? { kind: 'unknown' } : { kind: 'failed' });
      });
    return () => { alive = false; };
  }, [entry.id]);

  const go = async () => {
    setSaving(true);
    setProblem('');
    const result = await runSave(() => requeueCafEntryDecisions(entry.id));
    setSaving(false);
    if (!result.ok) {
      const notConfirmed = result.message === MSG_NOT_CONFIRMED;
      const message = notConfirmed
        ? "Couldn't confirm the re-run. Check Recent decisions before trying again."
        : result.message;
      setProblem(message);
      if (notConfirmed) setUnconfirmed(true);
      else toast.failed(message);
      return;
    }
    const requeued = result.value?.requeued ?? 0;
    toast.saved(requeued ? `Saved. ${formatCount(requeued)} emails will be decided again.` : 'Saved. There was nothing to re-run.');
    onClose();
  };

  const preview = state.kind === 'ready' ? state.preview : null;
  const count = preview?.would_requeue ?? 0;
  const canRun = !unconfirmed && (state.kind === 'unknown' || (preview !== null && !preview.over_cap && count > 0));
  return (
    <Modal title="Re-run decisions?" onClose={onClose} busy={saving}
      footer={unconfirmed ? (
        <button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={onClose}>Close</button>
      ) : (
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={saving}>Cancel</button>
          {canRun && (
            <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={saving} onClick={() => { void go(); }}>
              {saving ? 'Saving…' : preview ? `Re-run ${formatCount(count)} decisions` : 'Re-run decisions'}
            </button>
          )}
        </>
      )}>
      {state.kind === 'loading' && <p className="cur-hint">Counting the emails this example was used for…</p>}
      {state.kind === 'failed' && <p>Couldn&apos;t count the emails. Close this and try again.</p>}
      {state.kind === 'unknown' && (
        <p>The AI will decide again on the emails <b>{lenderOf(entry.sender) ?? senderEmail(entry.sender)}</b> was used for, using the example as it is now.</p>
      )}
      {preview && preview.over_cap && (
        <p>More than {formatCount(preview.matched)} emails used this example, which is too many to re-run from here. Ask support to run it.</p>
      )}
      {preview && !preview.over_cap && count === 0 && (
        <p>None of the emails this example was used for can be re-run.{preview.skipped_staff_touched ? ` ${formatCount(preview.skipped_staff_touched)} were handled by a person.` : ''}</p>
      )}
      {preview && !preview.over_cap && count > 0 && (
        <p>The AI will decide again on <b>{formatCount(count)} emails</b> this example was used for, using the example as it is now.</p>
      )}
      {(state.kind === 'unknown' || (preview && !preview.over_cap && count > 0)) && (
        <p className="cur-hint">
          Emails a person has handled{preview ? ` (${formatCount(preview.skipped_staff_touched)})` : ''} are left alone. If a new decision emails a client, it is sent as normal.
        </p>
      )}
      {problem && <div className="cur-alert cur-alert--error" role="alert">{problem}</div>}
    </Modal>
  );
};
